import type { TtqMembershipPressureRecommendation } from '../../services/ttqMembershipPressureService';

export const MEMBERSHIP_PRESSURE_RADAR_AXES = [
  { key: 'trunkProximity', label: '主干' },
  { key: 'typeHomogeneity', label: '同质' },
  { key: 'implicitAbility', label: '能力' },
  { key: 'total', label: '综合' },
] as const;

export const MEMBERSHIP_PRESSURE_RADAR_DEFAULT_THRESHOLDS = {
  trunkProximity: 0.2,
  typeHomogeneity: 0.12,
  implicitAbility: 0.15,
  total: 0.22,
};

const PEER_COLORS = ['#0369A1', '#C2410C', '#15803D', '#7C3AED', '#BE185D', '#0F766E', '#A16207'];
const THRESHOLD_COLOR = '#D97706';
const AVERAGE_COLOR = '#334155';

type AxisKey = (typeof MEMBERSHIP_PRESSURE_RADAR_AXES)[number]['key'];
type ScoreLike = TtqMembershipPressureRecommendation['scores'];
type Thresholds = NonNullable<TtqMembershipPressureRecommendation['radarThresholds']>;

type RadarSeries = {
  id: string;
  label: string;
  color: string;
  dashed?: boolean;
  filled?: boolean;
  scores: ScoreLike;
};

function clamp01(value: number | undefined): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function polar(cx: number, cy: number, radius: number, axisIndex: number, axisCount: number) {
  const angle = (-Math.PI / 2) + (axisIndex * 2 * Math.PI) / axisCount;
  return {
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  };
}

function polygonPath(values: number[], cx: number, cy: number, radius: number): string {
  return values
    .map((value, index) => {
      const point = polar(cx, cy, radius * clamp01(value), index, values.length);
      return `${index === 0 ? 'M' : 'L'}${point.x.toFixed(2)},${point.y.toFixed(2)}`;
    })
    .join(' ')
    .concat(' Z');
}

function axisValues(scores: ScoreLike): number[] {
  return MEMBERSHIP_PRESSURE_RADAR_AXES.map((axis) => clamp01(scores[axis.key]));
}

function thresholdValues(thresholds: Thresholds): number[] {
  return MEMBERSHIP_PRESSURE_RADAR_AXES.map((axis) => clamp01(thresholds[axis.key as keyof Thresholds] as number));
}

export function buildMembershipPressureRadarSeries(
  rec: TtqMembershipPressureRecommendation,
): { thresholds: Thresholds; series: RadarSeries[] } {
  const thresholds = rec.radarThresholds || MEMBERSHIP_PRESSURE_RADAR_DEFAULT_THRESHOLDS;
  const peers = rec.scoreSeries || [];
  const series: RadarSeries[] = peers.map((item, index) => ({
    id: item.particleId,
    label: item.label,
    color: PEER_COLORS[index % PEER_COLORS.length]!,
    filled: true,
    scores: item.scores,
  }));
  series.push({
    id: 'average',
    label: '同伴平均',
    color: AVERAGE_COLOR,
    dashed: true,
    scores: rec.scores,
  });
  return { thresholds, series };
}

type MembershipPressureRadarChartProps = {
  rec: TtqMembershipPressureRecommendation;
  size?: number;
};

export function MembershipPressureRadarChart({ rec, size = 360 }: MembershipPressureRadarChartProps) {
  const { thresholds, series } = buildMembershipPressureRadarSeries(rec);
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.32;
  const axisCount = MEMBERSHIP_PRESSURE_RADAR_AXES.length;
  const grid = [0.25, 0.5, 0.75, 1];

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width="100%"
      role="img"
      aria-label="入围分项雷达图"
      className="max-h-[380px] max-w-full"
    >
      {grid.map((level) => (
        <path
          key={level}
          d={polygonPath(MEMBERSHIP_PRESSURE_RADAR_AXES.map(() => level), cx, cy, radius)}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.18}
          strokeWidth={1}
        />
      ))}
      {MEMBERSHIP_PRESSURE_RADAR_AXES.map((axis, index) => {
        const end = polar(cx, cy, radius, index, axisCount);
        const labelPos = polar(cx, cy, radius + 36, index, axisCount);
        const threshold = clamp01(thresholds[axis.key as AxisKey]);
        const tick = polar(cx, cy, radius * threshold, index, axisCount);
        return (
          <g key={axis.key}>
            <line x1={cx} y1={cy} x2={end.x} y2={end.y} stroke="currentColor" strokeOpacity={0.28} strokeWidth={1} />
            <circle cx={tick.x} cy={tick.y} r={3.2} fill={THRESHOLD_COLOR} />
            <text
              x={labelPos.x}
              y={labelPos.y}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="11"
              fontWeight={700}
              fill="currentColor"
            >
              {axis.label}
            </text>
            <text
              x={labelPos.x}
              y={labelPos.y + 13}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="10"
              fill={THRESHOLD_COLOR}
            >
              阈 {threshold.toFixed(2)}
            </text>
          </g>
        );
      })}
      <path
        d={polygonPath(thresholdValues(thresholds), cx, cy, radius)}
        fill={THRESHOLD_COLOR}
        fillOpacity={0.08}
        stroke={THRESHOLD_COLOR}
        strokeWidth={2}
        strokeDasharray="5 4"
      />
      {series.map((item) => (
        <path
          key={item.id}
          d={polygonPath(axisValues(item.scores), cx, cy, radius)}
          fill={item.filled ? item.color : 'none'}
          fillOpacity={item.filled ? 0.1 : 0}
          stroke={item.color}
          strokeWidth={item.dashed ? 2 : 1.6}
          strokeDasharray={item.dashed ? '6 4' : undefined}
        />
      ))}
    </svg>
  );
}

type MembershipPressureRadarLegendProps = {
  rec: TtqMembershipPressureRecommendation;
};

export function MembershipPressureRadarLegend({ rec }: MembershipPressureRadarLegendProps) {
  const { series } = buildMembershipPressureRadarSeries(rec);
  return (
    <ul className="space-y-2 text-xs leading-5 text-ink">
      <li className="flex items-start gap-2">
        <span className="mt-1.5 h-0.5 w-6 shrink-0 rounded" style={{ background: THRESHOLD_COLOR, borderTop: `2px dashed ${THRESHOLD_COLOR}` }} />
        <span>过闸阈值（虚线橙框；轴上橙点也是该维阈值）</span>
      </li>
      {series.map((item) => (
        <li key={item.id} className="flex items-start gap-2">
          <span
            className="mt-1.5 h-0.5 w-6 shrink-0 rounded"
            style={{
              background: item.dashed ? 'transparent' : item.color,
              borderTop: item.dashed ? `2px dashed ${item.color}` : `2px solid ${item.color}`,
            }}
          />
          <span className="min-w-0">
            <span className="font-semibold">{item.label}</span>
            <span className="mt-0.5 block tabular-nums text-muted">
              主干 {clamp01(item.scores.trunkProximity).toFixed(2)}
              {' · '}
              同质 {clamp01(item.scores.typeHomogeneity).toFixed(2)}
              {' · '}
              能力 {clamp01(item.scores.implicitAbility).toFixed(2)}
              {' · '}
              综合 {clamp01(item.scores.total).toFixed(2)}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}
